import { Box } from '@eatclubapp/ec-component-library';
import { CircularProgress } from '@mui/material';
import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';
import ArrowUp from '../../assets/icons/arrow_up.svg';
import SearchInput from './SearchInput';
import TablePagination from './TablePagination';
import useStyles from './TableStyles';
import { COLORS } from '../../EatClubTheme';
import { devLog } from '../../utils';

/**
 * A wrapper for Tanstack table, mostly compatible with our previous material-table usage
 *
 * TODO clean up mix of inline styles and classes
 */
const Table = ({
  title,
  isLoading,
  columns,
  data,
  options,
  localization,
  hasPagination,
  onRowClick, // Callback to make clickable rows
  components, // Custom overrides for row or cell components
  className = '', // Extra styles to provide to the table to override
  paginationCallback = () => {}, // Callback when pagination changes
  searchCallback = () => {}, // Callback when search debounce happens
  defaultSorting = [], // Default state when sorting
  filter = null, // If provided, replaces the search in the table with what's provided from the parent
  setFilter = null, // If provided, replaces the search in the table with what's provided from the parent
  style,
  hasRowSelection,
  onRowSelection,
  manualPagination = false, // Serverside pagination
  rowCount = null,
  manualFiltering = false, // Serverside filters
  emptyContent = null, // Custom tbody empty state (replaces default message when row count is 0)
}) => {
  const classes = useStyles();

  const [sorting, setSorting] = React.useState(defaultSorting);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [rowSelection, setRowSelection] = useState({});

  const [pagination, setPagination] = useState({
    pageIndex: 0, // initial page index
    pageSize: hasPagination ? (options?.pageSize ?? 10) : 9999, // default page size
  });

  /**
   * Updates the table row selection state as well as passes the selected rows as their original data objects to the `onRowSelection` Table prop
   * @see https://github.com/TanStack/table/discussions/2155#discussioncomment-10056182
   */
  const handleOnRowSelectionChange = useCallback(
    (valueFn) => {
      if (typeof valueFn === 'function') {
        const updatedRowSelection = valueFn(rowSelection);
        setRowSelection(updatedRowSelection);

        const selectedRows = Object.keys(updatedRowSelection).reduce((acc, key) => {
          if (updatedRowSelection[key]) {
            const index = parseInt(key, 10);
            const row = data[index];
            if (row) {
              acc.push(row);
            }
          }
          return acc;
        }, []);

        devLog('info', 'Selected row(s):', selectedRows);
        onRowSelection?.(selectedRows);
      }
    },
    [data, onRowSelection, rowSelection],
  );

  // Search override
  useEffect(() => {
    setGlobalFilter(filter);
  }, [filter]);

  const handleFilterChange = (newValue) => {
    if (setFilter) {
      setFilter(newValue);
    } else {
      setGlobalFilter(newValue);
    }
  };

  // Show the loading row (spinner) instead of the empty message while fetching with no data yet
  const loadingRows = isLoading === true && (data?.length ?? 0) === 0;

  const emptyStateClass = loadingRows
    ? classes.emptyDataLoading
    : emptyContent
      ? classes.emptyDataCustom
      : classes.emptyData;

  const table = useReactTable({
    data,
    columns,
    isLoading,
    // debugTable: true,
    options,
    localization,
    sortDescFirst: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting, // optionally control sorting state in your own scope for easy access
    enabledRowSelection: hasRowSelection,
    onRowSelectionChange: handleOnRowSelectionChange,
    onGlobalFilterChange: handleFilterChange,
    manualPagination,
    manualFiltering,
    rowCount,
    state: {
      sorting,
      pagination,
      globalFilter,
      rowSelection,
    },
  });

  const pageSize = Math.min(table.getState().pagination?.pageSize, table.getRowCount());
  // Client side pagination callback
  useEffect(() => {
    if (!manualPagination && pageSize) {
      paginationCallback(pageSize);
    }
  }, [pageSize]);

  // Server pagination
  useEffect(() => {
    if (manualPagination) {
      paginationCallback(pagination.pageIndex, pagination.pageSize);
    }
  }, [manualPagination, pagination.pageIndex, pagination.pageSize]); // Dependencies

  return (
    <Box className={classes.tableContainer} style={{ ...(style ?? {}) }}>
      <Box>
        {title && <Box className={classes.title}>{title}</Box>}
        {options.search && (
          <Box className={classes.searchContainer}>
            <SearchInput
              value={globalFilter}
              setValue={(newValue) => {
                handleFilterChange(newValue);
                searchCallback(newValue);
              }}
            />
          </Box>
        )}
        <Box className={classes.tableOuter} style={{ ...(style?.tableOuter ?? {}) }}>
          <table
            className={`${classes.table} ${className}`}
            style={{
              borderCollapse: options?.separate ? 'separate' : 'collapse',
            }}
          >
            <thead style={{ ...(options?.headerStyle ?? {}) }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                          textAlign: `${header.column.columnDef.align ?? 'left'}`,
                          ...(header.column.columnDef?.headerStyle ?? {}),
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <Box
                            className={`${classes.header} ${
                              header.column.getCanSort() ? classes.sortableHeader : ''
                            }`}
                            style={{
                              flexDirection:
                                header.column.columnDef.align === 'right' ? 'row-reverse' : 'row',
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                            title={
                              header.column.getCanSort()
                                ? header.column.getNextSortingOrder() === 'asc'
                                  ? 'Sort ascending'
                                  : header.column.getNextSortingOrder() === 'desc'
                                    ? 'Sort descending'
                                    : 'Clear sort'
                                : undefined
                            }
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <div>
                              {header.column.getCanSort() &&
                                (header.column.getIsSorted() === 'asc' ? (
                                  <ArrowUp className={classes.arrowIcon} />
                                ) : header.column.getIsSorted() === 'desc' ? (
                                  <ArrowUp
                                    className={classes.arrowIcon}
                                    style={{ transform: 'rotate(180deg)' }}
                                  />
                                ) : (
                                  <ArrowUp
                                    className={`${classes.arrowIcon} ${classes.arrowIconHidden}`}
                                  />
                                ))}
                            </div>
                          </Box>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowCount() === 0 && (
                <tr>
                  <td colSpan={table.getAllColumns().length} className={emptyStateClass}>
                    {loadingRows ? (
                      <Box className={`${classes.emptyStateEnter} ${classes.loadingRow}`}>
                        <CircularProgress
                          size={22}
                          sx={{ color: COLORS.BRAND_YELLOW }}
                          thickness={4}
                        />
                        <Box component='span' className={classes.loadingLabel}>
                          {localization?.body?.loadingMessage ?? 'Loading…'}
                        </Box>
                      </Box>
                    ) : emptyContent ? (
                      <Box className={`${classes.emptyStateEnter} ${classes.emptyStateSlotBody}`}>
                        {emptyContent}
                      </Box>
                    ) : (
                      (localization?.body?.emptyDataSourceMessage ?? 'No items to display')
                    )}
                  </td>
                </tr>
              )}
              {table.getRowModel().rows.map((row) => {
                // Allow overriding rows with custom components
                if (components?.Row) {
                  const { Row } = components;
                  return <Row row={row} onRowClick={onRowClick} />;
                }
                return <TableRow key={row.id} row={row} />;
              })}
            </tbody>
          </table>
        </Box>
      </Box>

      {hasPagination && <TablePagination table={table} options={options} />}
    </Box>
  );
};

export default Table;

/**
 * A row in the table. Can be clickable if a callback provided
 */
export const TableRow = ({ row, onRowClick }) => {
  const classes = useStyles();

  return (
    <tr
      className={`${classes.row} ${onRowClick && classes.clickableRow}`}
      onClick={(e) => onRowClick && onRowClick(e, row.original)}
    >
      {row.getVisibleCells().map((cell) => {
        return (
          <td
            key={cell.id}
            style={{
              width: `${cell.column.columnDef?.width ?? cell.column.columnDef?.size ?? 'auto'}`,
              textAlign: `${cell.column.columnDef.align ?? 'left'}`,
              ...(cell.column.columnDef?.cellStyle ?? {}),
            }}
          >
            <div
              className={classes.tdContents}
              style={{
                // Setting width here too since tables often ignore td width setting
                width: `${cell.column.columnDef?.width ?? cell.column.columnDef?.size ?? 'auto'}`,
                boxSizing: 'border-box',
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          </td>
        );
      })}
    </tr>
  );
};
